import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import {
  CuttingMatBaseGroup,
  ICustomResponse,
  ICuttingBaseMaterial,
  MaterialBaseService,
  MaterialType,
} from 'cloud-shared-lib';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';

export interface CuttingAllMaterialBaseDialogData {
  materialsToExclude?: ICuttingBaseMaterial[];
  multiple?: boolean;
}

@Component({
  templateUrl: 'cutting-all-material-base-dialog.component.html',
  styleUrls: ['cutting-all-material-base-dialog.component.scss'],
})
export class CuttingAllMaterialBaseDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  dataSource: MatTableDataSource<ICuttingBaseMaterial> | null;
  fixedType: MaterialType = undefined;
  displayedColumns = ['select', 'name', 'article', 'price'];
  unsubscribe$: Subject<void> = new Subject();
  loadMore$: BehaviorSubject<[number, number]> = new BehaviorSubject([0, 25]);
  loading = false;

  find$: BehaviorSubject<string> = new BehaviorSubject('');
  modifiers$: Observable<string>;

  currentGroupId = 0;
  focusedGroupId = -1;
  materials: ICuttingBaseMaterial[] = [];
  materialsTotlaCount = 0;

  selectedMaterials: ICuttingBaseMaterial[] = [];

  groups: CuttingMatBaseGroup[] = [];
  data$: Observable<
    ICustomResponse<{
      materialsDTO: ICuttingBaseMaterial[];
      materialsCount: number;
    }>
  >;

  get hasMore(): boolean {
    return this.materialsTotlaCount - this.dataSource.data.length > 0;
  }

  constructor(
    public dialogRef: MatDialogRef<CuttingAllMaterialBaseDialogComponent>,
    private materialBaseService: MaterialBaseService,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: CuttingAllMaterialBaseDialogData
  ) {}

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource();
    this.getMaterialGroups();
  }

  ngAfterViewInit(): void {
    this.addListners();
  }

  onGroupSelect(group: CuttingMatBaseGroup): void {
    this.currentGroupId = group.id;
    this.dataSource.data = [];
    this.loadMore$.next([0, 25]);
  }

  getMaterialGroups(): void {
    this.materialBaseService
      .getMatBaseMaterialGroups()
      .pipe(catchError(() => (this.groups = [])))
      .subscribe(g => {
        this.groups = g || [];
        if (g.length > 0) {
          this.focusedGroupId = g[0].id;
        }
      });
  }

  toggleSelection(item: ICuttingBaseMaterial): void {
    if (this.isExcludeExists(item)) {
      return;
    }

    const itemIndex = this.selectedMaterials.findIndex(
      (it: ICuttingBaseMaterial) =>
        it.name.toLowerCase().trim() === item.name.toLowerCase().trim() &&
        it.article.toLowerCase().trim() === item.article.toLowerCase().trim()
    );

    if (itemIndex !== -1) {
      this.selectedMaterials.splice(itemIndex, 1);
    } else {
      if (!this.data?.multiple) {
        this.selectedMaterials = [];
      }

      this.selectedMaterials.push(item);
    }
  }

  saveAndClose(): void {
    this.dialogRef.close(this.selectedMaterials);
  }

  isSelected(item: ICuttingBaseMaterial): boolean {
    return this.selectedMaterials.some(
      it =>
        it.name.toLowerCase().trim() === item.name.toLowerCase().trim() &&
        it.article.toLowerCase().trim() === item.article.toLowerCase().trim()
    );
  }

  isExcludeExists(item: ICuttingBaseMaterial): boolean {
    return this.data?.materialsToExclude?.some(
      m =>
        m.name.toLowerCase().trim() === item.name.toLowerCase().trim() &&
        m.article.toLowerCase().trim() === item.article.toLowerCase().trim()
    );
  }

  showFullNameTooltip(el: HTMLElement): boolean {
    if (el.offsetWidth < el.scrollWidth) {
      return true;
    } else {
      return false;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  private getMaterials(): void {
    if (!this.currentGroupId) {
      return;
    }

    this.loading = true;

    const filterText = this.find$.value.replace(/'/g, '');

    this.data$ = this.materialBaseService
      .getMatBaseMaterials(this.currentGroupId, this.loadMore$.value[1], this.loadMore$.value[0], filterText)
      .pipe(
        tap(response => {
          this.loading = false;
          if (response?.result) {
            this.dataSource.data.push(...response.result.materialsDTO);
            this.dataSource.data = [...this.dataSource.data];

            this.materialsTotlaCount = response.result.materialsCount;
          } else if (response?.error) {
            this.snackBar.open($localize`Не удалось загрузить материалы`);
          }
        })
      );
  }

  private addListners(): void {
    this.modifiers$ = this.find$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => {
        this.dataSource.data = [];
        this.loadMore$.next([0, 25]);
      })
    );

    this.loadMore$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.getMaterials();
    });
  }
}
